/**
 * Billing-period helpers — the shared period control on the Pipeline page.
 *
 * Phase 12 / Task 12.3 — a billing period is a half-open month range
 * [periodStart, periodEnd): the first day 00:00 LOCAL of the selected month
 * through the first day 00:00 LOCAL of the NEXT month. Both bounds are emitted
 * as ISO strings (`toISOString()` → UTC 'Z') to satisfy the API's
 * `z.string().datetime()` body schema (invoices.ts generate route).
 *
 * The control's value is a native `<input type="month">` string: "YYYY-MM".
 *
 * #50 — the month lives in the URL (`?month=YYYY-MM`) so the Pipeline, a customer's
 * drill-down and a ticket page all show the same month, and Back keeps it.
 */

import { useSearchParams } from 'react-router-dom';

export interface BillingPeriod {
  /** The "YYYY-MM" control value. */
  readonly value: string;
  /** ISO timestamp — first day of the month at 00:00 local. */
  readonly periodStart: string;
  /** ISO timestamp — first day of the NEXT month at 00:00 local (exclusive end). */
  readonly periodEnd: string;
}

/** The current calendar month as a "YYYY-MM" control value (local time). */
export function currentMonthValue(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Convert a "YYYY-MM" control value into a half-open [start, end) period.
 * Returns null when the value is malformed (empty / not a valid month).
 */
export function periodFromMonthValue(value: string): BillingPeriod | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (match === null) return null;
  const year = Number(match[1]);
  const month0 = Number(match[2]) - 1; // 0-indexed month
  if (month0 < 0 || month0 > 11) return null;
  const start = new Date(year, month0, 1, 0, 0, 0, 0);
  const end = new Date(year, month0 + 1, 1, 0, 0, 0, 0);
  return { value, periodStart: start.toISOString(), periodEnd: end.toISOString() };
}

/**
 * The selected billing month, kept in `?month=`. A missing or malformed value reads as
 * the current month. Changing it replaces the history entry, so Back leaves the page
 * rather than stepping through every month tried.
 */
export function useBillingMonth(): readonly [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('month');
  const value = raw !== null && periodFromMonthValue(raw) !== null ? raw : currentMonthValue();
  const setValue = (next: string): void => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set('month', next);
        return out;
      },
      { replace: true },
    );
  };
  return [value, setValue] as const;
}
